import type { PodBrand } from '@/types/pod'

/**
 * The Foredesk pod — branding layer (browser side).
 *
 * This is the self-referential pod in its finished form: Foredesk selling
 * Foredesk, embedded above the fold on foredesk.co.uk. It supersedes the
 * earlier `chamberflow` pod (same job, pre-naming); that pod is retained until
 * this one is proven live, then removed in the cutover batch — prove the new
 * path before closing the old one.
 *
 * CONTACT DETAILS ARE CONFIRMED (19 Jul 2026): the operator's real, monitored
 * inbox. Contact values are where the assistant points a prospect when it
 * defers to a human — config, not a grounding-fence claim.
 *
 * POSITIONING (locked): Foredesk is a lead generation system — the UI strings
 * below never use the word the site bans; the conversation is how it works,
 * not what it is.
 */
export const foredeskPod: PodBrand = {
  id: 'foredesk',
  brandName: 'Foredesk',
  productName: 'Foredesk',
  assistantName: 'Bell',
  tagline: 'The front of house for your website — every visitor greeted, every enquiry captured.',
  roleLabel: 'front of house',
  welcomeIntro:
    "I can tell you what Foredesk does, what it costs, and how it captures the enquiries your website is currently losing. And if you'd like to see it running on your own site, I'll take your details and Mick will call you back. What would you like to know?",
  starters: [
    'What is Foredesk?',
    'How much does it cost?',
    'Will it work for my business?',
    'How do I get a demo?',
  ],
  inputPrompt: 'Ask Bell about Foredesk',
  footerNote:
    "Bell answers only from Foredesk's own published material — never invented. Anything outside it goes straight to the team.",
  theme: {
    // The Foredesk palette — spruce, porcelain, brass — matching the
    // foredesk.co.uk design tokens exactly, so the embedded panel and the page
    // that frames it read as one brand.
    '--color-brand-primary': '18 49 43', // spruce #12312B
    '--color-brand-primary-hover': '12 34 30', // spruce-deep #0C221E
    '--color-brand-accent': '194 154 75', // brass #C29A4B
    '--color-surface-base': '247 248 247', // porcelain #F7F8F7
    '--color-surface-raised': '255 255 255',
    '--color-surface-subtle': '237 241 239',
    '--color-text-primary': '26 31 29', // ink #1A1F1D
    '--color-text-muted': '85 98 93', // slate #55625D
    '--color-text-danger': '178 47 47',
    '--color-border-subtle': '227 232 230', // mist #E3E8E6
  },
  contact: {
    email: 'mick@autaimate.com',
  },
}
